import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';
import cx from 'classnames';

import Button from '~/components/Button';
import Input from '~/components/Forms/Input';
import Typography from '~/components/Typography';
import type { Publication } from '~/interfaces/publication';
import type { PricingDetails } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';
import currencyFormatter from '~/pages/upgrade/utils/currencyFormatter';

import UpgradeForm from '../../UpgradeForm';

interface Props {
  onCancel: () => void;
  selectedPriceId: string;
  upgradePage: UpgradePage;
  publication: Publication;
  pricingDetails: PricingDetails;
  isExternalCheckoutEnabled: boolean;
}

const GiftForm = ({
  onCancel,
  selectedPriceId,
  upgradePage,
  publication,
  pricingDetails,
  isExternalCheckoutEnabled,
}: Props) => {
  const { t } = useTranslation();
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [purchaserName, setPurchaserName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const minDate = new Date().toISOString().split('T')[0];
  // The max date is 45 days from today
  const maxDate = new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startAt, setStartAt] = useState<string>(minDate);
  const selectedTier = pricingDetails.tiers.find((tier) => tier.prices.find((price) => price.id === selectedPriceId));
  const selectedPrice = selectedTier?.prices.find((price) => price.id === selectedPriceId);
  const formattedPrice = currencyFormatter({
    currency: selectedPrice?.currency,
  }).format((selectedPrice?.amount_cents || 0) / 100);

  const interval = selectedPrice?.interval === 'year' ? t('upgrade.interval.year') : t('upgrade.interval.month');

  const isValid = purchaserEmail && purchaserName && recipientEmail;

  const fetcher = useFetcher<{
    redirect_url: string;
    toast: any;
  }>();
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const isSuccess = fetcherData?.toast?.status === 'success';

  useEffect(() => {
    if (isSuccess) {
      window.location.replace(fetcherData.redirect_url);
    }
  }, [fetcherData]);

  const handleRedirectAfterPayment = () => {
    window.location.replace('/'); // Redirect to home page
    return;
  };

  return (
    <div className="flex flex-col space-y-6">
      <fetcher.Form method="patch" action="/upgrade" className="relative flex w-full max-w-sm flex-col gap-y-2">
        <Typography
          as="div"
          size={upgradePage.main_section.description_font_size}
          color={upgradePage.main_section.description_color}
          weight="regular"
          fontFamily={upgradePage.main_section.description_font_family}
          className={cx({
            'w-full max-w-2xl text-center': true,
          })}
        >
          {t('upgrade.tabs.gift.form.title', {
            price: formattedPrice,
            interval,
            tier_name: selectedTier?.name,
          })}
        </Typography>
        <Input
          label={t('upgrade.tabs.gift.form.purchaser_name')}
          labelColor={upgradePage.main_section.description_color}
          fontColor={upgradePage.main_section.description_color}
          name="purchaser_name"
          value={purchaserName}
          onChange={(e) => setPurchaserName(e.target.value)}
          required
        />
        <Input
          label={t('upgrade.tabs.gift.form.purchaser_email')}
          labelColor={upgradePage.main_section.description_color}
          fontColor={upgradePage.main_section.description_color}
          name="email"
          value={purchaserEmail}
          type="email"
          onChange={(e) => setPurchaserEmail(e.target.value)}
          required
        />
        <Input
          label={t('upgrade.tabs.gift.form.recipient_email')}
          labelColor={upgradePage.main_section.description_color}
          fontColor={upgradePage.main_section.description_color}
          name="recipient_email"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          required
        />
        <div className="space-y-1">
          <label
            htmlFor={message}
            className="block text-sm font-medium text-gray-700 text-wt-text-on-background"
            style={{
              color: upgradePage.main_section.description_color || '#374151',
            }} // #374151 is text-gray-700
          >
            {t('upgrade.tabs.gift.form.message')}
          </label>
          <textarea
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="block w-full rounded-wt border-gray-300 bg-transparent text-sm shadow-sm placeholder:text-sm focus:border-wt-primary focus:ring-wt-primary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            style={{
              color: upgradePage.main_section.description_color || '#374151',
            }} // #374151 is text-gray-700
          />
        </div>
        <Input
          label={t('upgrade.tabs.gift.form.start_at')}
          labelColor={upgradePage.main_section.description_color}
          fontColor={upgradePage.main_section.description_color}
          name="start_at"
          type="date"
          value={startAt}
          min={minDate}
          max={maxDate}
          onChange={(e) => setStartAt(e.target.value)}
        />
        <input type="hidden" name="isGift" value={'true'} />
        <input type="hidden" name="price_id" value={selectedPriceId} />
        <input type="hidden" name="upgrade_error_message" value={t('upgrade.error_message')} />

        {isExternalCheckoutEnabled && (
          <div className="flex flex-col justify-end space-y-2 text-center">
            <Button type="submit" disabled={!isValid || isSubmitting || isSuccess}>
              {isSubmitting ? t('shared.actions.submitting') : t('shared.cta.continue')}
            </Button>
            <Button variant="underline" onClick={onCancel}>
              {t('shared.cta.back')}
            </Button>
          </div>
        )}
      </fetcher.Form>
      {!isExternalCheckoutEnabled && (
        <div className="flex flex-col justify-end space-y-2 text-center">
          <UpgradeForm
            priceId={selectedPriceId}
            premiumOfferId={selectedPrice?.premium_offer?.id}
            upgradePage={upgradePage}
            pricingDetails={pricingDetails}
            onContinue={handleRedirectAfterPayment}
            publication={publication}
            disabled={!isValid}
            giftForm={{
              email: purchaserEmail,
              purchaser_name: purchaserName,
              recipient_email: recipientEmail,
              message,
              start_at: startAt,
            }}
          />
          <Button variant="underline" onClick={onCancel}>
            {t('shared.cta.back')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GiftForm;
